<template>
  <div>
  <SelfLinkCallback v-if="linking" />
  <main v-else class="min-h-screen flex items-center justify-center p-6">
    <div class="max-w-md text-center">
      <h1 class="text-xl font-semibold">{{ connectionTest ? 'University sign-in test complete' : errorCode === 'sso_retry' ? 'Please try signing in again' : errorCode === 'access_pending' ? 'Your sign-in is verified' : error ? 'Access is not ready' : 'Signing in' }}</h1>
      <p v-if="connectionTest" class="mt-4">University authentication completed successfully. No product access has been granted. Return to the onboarding tab and record this result with the email mapping, stable NameID and MFA evidence from university IT.</p>
      <p v-if="error" class="mt-4" :class="errorCode === 'access_pending' ? 'text-gray-700' : 'text-red-700'">{{ error }}</p>
      <div v-if="tenants.length" class="mt-4 text-left">
        <p class="text-gray-600">Choose the organisation you want to work in.</p>
        <div class="mt-3 space-y-2">
          <button
            v-for="tenant in tenants"
            :key="tenant"
            type="button"
            :disabled="selectingTenant"
            class="w-full border rounded p-3 text-left hover:bg-gray-50 disabled:opacity-50"
            @click="selectTenant(tenant)"
          >
            {{ tenant }}
          </button>
        </div>
      </div>
      <form v-if="challengeId" class="mt-4" @submit.prevent="verifyMfa">
        <p class="text-gray-600">Enter the code sent to {{ email }}.</p>
        <input
          v-model="mfaCode"
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          pattern="[0-9]{6}"
          maxlength="6"
          required
          class="mt-3 border rounded p-2 w-full"
          aria-label="Verification code"
        />
        <button type="submit" class="mt-3 bg-indigo-600 text-white rounded px-4 py-2">
          Verify code
        </button>
      </form>
      <p v-else-if="!connectionTest && !error && !tenants.length" class="mt-4 text-gray-600">Checking your MGC access…</p>
      <router-link v-if="error" :to="{ path: '/admin/login', query: { sso: '1', connection_test: retryTestDomain || undefined } }" class="inline-block mt-4 text-indigo-700">
        Back to sign in
      </router-link>
    </div>
  </main>
  </div>
</template>

<script>
import axios from "axios";
import SelfLinkCallback from "./SelfLinkCallback.vue";
import { isLinkCallback } from "../../api/selfServiceLink";
import Auth from "../../api/auth.js";
import { finishSharedSignIn } from "../../api/sharedSso.js";

export default {
  name: "SsoCallback",
  components: { SelfLinkCallback },
  data: () => ({
    linking: isLinkCallback(), connectionTest: false, retryTestDomain: "", error: "", errorCode: "", challengeId: "", email: "", mfaCode: "", destination: "/admin/choose",
    accessToken: "", tenants: [], selectingTenant: false,
  }),
  async mounted() {
    if (this.linking) return;
    try {
      const { accessToken, destination, connectionTest } = await finishSharedSignIn();
      if (connectionTest) { this.connectionTest = true; return; }
      this.accessToken = accessToken;
      this.destination = destination;
      await this.exchange();
    } catch (error) {
      const code = error?.response?.data?.code || error.code;
      this.retryTestDomain = error.connectionTest || "";
      this.errorCode = code || 'error';
      if (code === 'access_pending') {
        this.error = 'We are linking your approved MGC account. You do not need to try again now. We will email you when access is ready.';
      } else if (code === 'sso_retry') {
        this.error = 'Your sign-in attempt expired or was interrupted. Start again using organisation sign-in. If it keeps happening, contact your MGC administrator.';
      } else if (code === 'no_access') {
        this.error = 'Your organisation sign-in worked, but you do not have MGC access. Contact your MGC administrator to request it.';
      } else {
        this.error = 'Shared sign-in could not be completed. Ask your MGC administrator to check your access.';
      }
    }
  },
  methods: {
    async exchange(tenant) {
      const request = { access_token: this.accessToken };
      if (tenant) request.tenant = tenant;
      const { data } = await axios.post("/sso/exchange", request);
      if (data.tenant_selection_required) {
        this.tenants = data.tenants;
        return;
      }
      this.tenants = [];
      if (data.mfa_required) {
        this.challengeId = data.challenge_id;
        this.email = data.email;
        this.accessToken = "";
        return;
      }
      this.accessToken = "";
      await Auth.setTokensForLoggedIn(data.tokens, false, true);
      await this.$router.replace(this.destination);
    },
    async selectTenant(tenant) {
      this.error = "";
      this.selectingTenant = true;
      try {
        await this.exchange(tenant);
      } catch (error) {
        this.error = "This organisation could not be opened. Ask your MGC administrator to check your access.";
      } finally {
        this.selectingTenant = false;
      }
    },
    async verifyMfa() {
      this.error = "";
      try {
        await Auth.verifyMfa({ challenge_id: this.challengeId, code: this.mfaCode }, false, true);
        await this.$router.replace(this.destination);
      } catch (error) {
        this.error = "The verification code is invalid or expired.";
      }
    },
  },
};
</script>
